var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { nothing } from 'lit';
import { MobxLitElement } from '@adobe/lit-mobx';
import { runInAction } from 'mobx';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import comsStore from '../state/ComsStore';
import Service from '../util/Service';
import { groupTemplate, groupTemplateOverview } from './templates/group.tmpl';
import { historyTemplate } from './templates/history.tmpl';
import Tracker from '../classes/Tracker';
import { referencesTemplate } from './templates/references.tmpl';
import { replaceTooltips } from './templates/util';
/**
 * The custom element `<tef-coms4cs>`.
 */
export class TefComs4csMicroFrontend extends MobxLitElement {
    constructor() {
        super(...arguments);
        this.apiUri = '';
        this.agentToken = '';
        this.gatewayToken = '';
        this.lifecycle = '';
        this.mode = '';
        this.traceid = '';
        this.occlUri = '';
        this.styleContext = '';
        this.consentwhitelist = '';
        this.channel = '';
        this.usertype = '';
        this.vonr = '';
        this.references = '';
        this.showReferences = false;
        this.showVersions = false;
        this.isExistingRegister = false;
        this.spId = '';
        this.lcId = '';
        this.registerId = '';
        this.fetch = '';
        this.store = comsStore;
        this.service = new Service(this); // binding service to <tef-coms4cs>
        this.tracker = new Tracker();
    }
    async connectedCallback() {
        var _a, _b;
        try {
            comsStore.reset();
            // show more infos if debugmode is on
            if (localStorage.getItem('tef-coms-4cs-debug-mode')) {
                let timestampInit = 0;
                let timestampReady = 0;
                this.addEventListener('tef-coms4cs', (e) => {
                    const { detail } = e;
                    const channel = detail.level === 'error' ? 'error' : 'info';
                    console[channel]('tef-coms: event', detail.name, e);
                    if (detail.name === 'config/INIT') {
                        timestampInit = (performance && performance.now) ? performance.now() : 0;
                    }
                    if (detail.name === 'load/READY') {
                        timestampReady = (performance && performance.now) ? performance.now() : 0;
                        //const el = <TefComs4csMicroFrontend>customElements.get('tef-coms4cs');
                        const infoVersion = `version ${TefComs4csMicroFrontend.version}`;
                        const infoReady = `Micro-frontend ready in ${Math.trunc(timestampReady - timestampInit)} ms, `;
                        console.log(infoReady + infoVersion);
                    }
                });
            }
            // issue 'config/INIT' immediately because it is used for performance measurements
            this.service.fireEvent({
                name: 'config/INIT',
                level: 'info',
                message: 'micro-frontend is initializing',
            });
            super.connectedCallback(); // start rendering immediately !!
            await this.store.init({
                apiUri: this.apiUri,
                agentToken: this.agentToken,
                gatewayToken: this.gatewayToken,
                lifecycle: this.lifecycle,
                mode: this.mode,
                traceid: this.traceid,
                occlUri: this.occlUri,
                styleContext: this.styleContext,
                consentwhitelist: this.consentwhitelist,
                channel: this.channel,
                usertype: this.usertype,
                vonr: this.vonr,
                references: this.references,
                showReferences: this.showReferences,
                showVersions: this.showVersions,
                isExistingRegister: this.isExistingRegister,
                spId: this.spId,
                lcId: this.lcId,
                registerId: this.registerId,
                fetch: this.fetch,
            });
            (_a = this.shadowRoot) === null || _a === void 0 ? void 0 : _a.addEventListener('occl.collapsible.open', (event) => {
                const { id: name } = event === null || event === void 0 ? void 0 : event.target;
                this.tracker.clickCollapsable(name);
                const group = this.store.findGroupByName(name);
                if (group) {
                    runInAction(() => {
                        group.collapsibleOpen = true;
                    });
                }
            });
            const { history } = comsStore;
            (_b = this.shadowRoot) === null || _b === void 0 ? void 0 : _b.addEventListener('occl.pager.open', (event) => history === null || history === void 0 ? void 0 : history.gotoPage(event.detail));
        }
        catch (error) {
            // ignore -- has error condition in store
        }
    }
    static get version() {
        return '__version__'; // patched by rollup
    }
    async save() {
        await this.store.save();
    }
    getConsents() {
        return this.store.getShortConsents();
    }
    getConsentStatus() {
        return this.store.getConsentStatus();
    }
    getReferenceLength() {
        var _a;
        return ((_a = this.store.references) === null || _a === void 0 ? void 0 : _a.length) || 0;
    }
    render() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        // If fatal error condition (not recoverable), render nothing.
        // Render stylesheet link as early as possible (for performance reasons).
        // Prevent FOUC effect after loading the OCCL resources.
        if (this.store.fatal) {
            return html `<!-- fatal error condition -->`;
        }
        const isLoading = (!this.store.isDataLoaded && ((_a = this.store.client) === null || _a === void 0 ? void 0 : _a.processing))
            || (this.store.occl && !this.store.occl.ready);
        const occlClasses = (_c = (_b = this.store.occl) === null || _b === void 0 ? void 0 : _b.getStyleContextClasses()) !== null && _c !== void 0 ? _c : [];
        const classes = [
            ...occlClasses,
            'tef-coms',
            'section',
            'section--size-flat',
        ];
        /* eslint-disable @typescript-eslint/indent */
        return html `
      ${(_d = this.store.occl) === null || _d === void 0 ? void 0 : _d.renderStylesheet()}
      ${isLoading
            ? nothing
            : html `
          <div class=${classes.join(' ')}>
            ${this.showReferences ? referencesTemplate(this.store.references) : nothing}
            ${((_e = this.store.history) === null || _e === void 0 ? void 0 : _e.configId)
                ? historyTemplate()
                : (_f = this.store.consentGroups) === null || _f === void 0 ? void 0 : _f.map((group) => (this.mode === 'overview'
                    ? groupTemplateOverview(group)
                    : groupTemplate(group)))}
            ${(((_h = (_g = this.store.consentGroups) === null || _g === void 0 ? void 0 : _g.length) !== null && _h !== void 0 ? _h : 0) > 0 && ((_j = this.store.details) === null || _j === void 0 ? void 0 : _j.pageFooter)) ?
                html `<p>${unsafeHTML(replaceTooltips(this.store.details.pageFooter))}</p>`
                : nothing}
          </div>
        `}
      ${this.mode === 'buttons' && !((_k = this.store.identity) === null || _k === void 0 ? void 0 : _k.readonly)
            ? html `<div class="section section-buttons">
        <button
          class="button button-default"
          .disabled=${!this.store.hasModifiedConsents}
          @click=${async () => {
                await this.store.save();
            }}
        >
          Speichern
        </button>
      </div>` : nothing}
    `;
    }
}
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "apiUri", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "agentToken", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "gatewayToken", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "lifecycle", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "mode", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "traceid", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "occlUri", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "styleContext", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "consentwhitelist", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "channel", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "usertype", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "vonr", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "references", void 0);
__decorate([
    property({ type: Boolean })
], TefComs4csMicroFrontend.prototype, "showReferences", void 0);
__decorate([
    property({ type: Boolean })
], TefComs4csMicroFrontend.prototype, "showVersions", void 0);
__decorate([
    property({ type: Boolean })
], TefComs4csMicroFrontend.prototype, "isExistingRegister", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "spId", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "lcId", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "registerId", void 0);
__decorate([
    property({ type: String })
], TefComs4csMicroFrontend.prototype, "fetch", void 0);
//# sourceMappingURL=tef-coms4cs.js.map